import { BUSINESS_MODELS } from '../data/models';
import { CRITERIA } from '../data/criteria';
function clamp(v, min = 0, max = 10) {
    return Math.max(min, Math.min(max, v));
}
export function calculateModelScore(model, criteria) {
    let sum = 0;
    let totalWeight = 0;
    for (const c of criteria) {
        const note = model.notes?.[c.id] ?? 5;
        const weighted = c.weight * note;
        sum += c.inverted ? c.weight * (10 - note) : weighted;
        totalWeight += c.weight;
    }
    return totalWeight > 0 ? sum / totalWeight : 5;
}
export function calculateCriterionCategoryScore(criteria, category) {
    const catCriteria = criteria.filter(c => c.category === category);
    if (catCriteria.length === 0)
        return 5;
    let sum = 0;
    for (const c of catCriteria) {
        const note = 5; // placeholder, replaced by caller
        sum += c.weight * note;
    }
    return sum / catCriteria.reduce((a, c) => a + c.weight, 0);
}
/**
 * IMN – Índice do Modelo de Negócio (25%)
 * Baseado em Market + Financial
 */
export function calcIMN(model) {
    const marketCrits = CRITERIA.filter(c => c.category === 'market');
    const finCrits = CRITERIA.filter(c => c.category === 'financial');
    const all = [...marketCrits, ...finCrits];
    return calculateModelScore(model, all);
}
/**
 * IAP – Índice de Adequação ao Perfil (25%)
 * Baseado em Technical + Experience skills do perfil
 */
export function calcIAP(model, profile) {
    const techNotes = profile.attributes.find(a => a.attributeId === 'tech_skill')?.value;
    const progNotes = profile.attributes.find(a => a.attributeId === 'programming')?.value;
    const bizNotes = profile.attributes.find(a => a.attributeId === 'business_exp')?.value;
    const techScore = (techNotes ?? 5) / 10;
    const progScore = (progNotes ?? 5) / 10;
    const bizScore = (bizNotes ?? 5) / 10;
    // Modelos tech-heavy penalizam perfil não-técnico
    const isTechModel = model.tags?.includes('tech') ?? false;
    const isServiceModel = model.tags?.includes('service') ?? false;
    let compatibility = 0.5;
    if (isTechModel) {
        compatibility = (techScore * 0.6 + progScore * 0.4);
    }
    else if (isServiceModel) {
        compatibility = (bizScore * 0.7 + techScore * 0.3);
    }
    else {
        compatibility = (techScore * 0.5 + bizScore * 0.5);
    }
    return clamp(compatibility * 10, 0, 10);
}
/**
 * ICP – Índice de Compatibilidade Pessoal (20%)
 * Baseado em gosto, propósito, personalidade
 */
export function calcICP(model, profile) {
    const writing = profile.attributes.find(a => a.attributeId === 'writing')?.value;
    const video = profile.attributes.find(a => a.attributeId === 'video')?.value;
    const risk = profile.attributes.find(a => a.attributeId === 'risk_tolerance')?.value;
    const stress = profile.attributes.find(a => a.attributeId === 'stress_tolerance')?.value;
    const motivation = profile.attributes.find(a => a.attributeId === 'motivation')?.value;
    let score = 5;
    // Afinidade com conteúdo
    const contentModel = model.tags?.includes('content') ?? false;
    const communityModel = model.tags?.includes('engajamento') ?? false;
    if (contentModel) {
        const w = writing ?? 5;
        const v = video ?? 5;
        score += (w + v) / 20; // 0 a 1 extra
    }
    if (communityModel) {
        const s = stress ?? 5;
        score += (s - 5) / 10; // penaliza baixa tolerância
    }
    // Risco
    const riskNum = risk ?? 5;
    const highRiskModel = model.tags?.includes('high-risk') ?? false;
    if (highRiskModel) {
        score += (riskNum - 5) / 10;
    }
    // Motivação alinhada
    const motivTags = model.tags ?? [];
    if (motivation === 'money' && motivTags.includes('commission'))
        score += 0.5;
    if (motivation === 'freedom' && motivTags.includes('passive'))
        score += 0.5;
    if (motivation === 'impact' && motivTags.includes('education'))
        score += 0.5;
    return clamp(score, 0, 10);
}
/**
 * IRD – Índice de Recursos Disponíveis (15%)
 * Baseado em capital, tempo, equipamento, networking
 */
export function calcIRD(model, profile) {
    const capital = profile.attributes.find(a => a.attributeId === 'capital')?.value;
    const freeTime = profile.attributes.find(a => a.attributeId === 'free_time')?.value;
    const equipment = profile.attributes.find(a => a.attributeId === 'equipment')?.value;
    const networking = profile.attributes.find(a => a.attributeId === 'networking')?.value;
    let score = 5;
    // Capital vs investimento
    const lowInvestment = model.tags?.includes('low-investment') ?? false;
    if (capital !== undefined) {
        if (lowInvestment && capital < 500)
            score += 1;
        else if (capital > 10000 && !lowInvestment)
            score += 1;
    }
    // Tempo disponível
    const timeIntensive = model.tags?.includes('time-for-money') ?? false;
    if (freeTime !== undefined) {
        if (timeIntensive && freeTime < 5)
            score -= 1;
        else if (!timeIntensive && freeTime > 10)
            score += 1;
    }
    // Equipamento
    if (equipment === 'poor')
        score -= 0.5;
    if (equipment === 'good')
        score += 0.5;
    // Networking
    const netScore = (networking ?? 5) / 10;
    score += netScore;
    return clamp(score, 0, 10);
}
/**
 * IEN – Índice de Esforço Necessário (15%) (INVERTIDO)
 * Baseado em complexidade, curva de aprendizado, tempo até 1º lucro
 */
export function calcIEN(model) {
    // Esperado: modelos mais simples têm IEN alto (baixo esforço)
    const complexModel = model.tags?.includes('complex') ?? false;
    const techModel = model.tags?.includes('tech') ?? false;
    const serviceModel = model.tags?.includes('service') ?? false;
    const timeForMoney = model.tags?.includes('time-for-money') ?? false;
    let effortScore = 5; // 5 = esforço moderado
    if (complexModel)
        effortScore -= 2;
    if (techModel)
        effortScore -= 1.5;
    if (serviceModel)
        effortScore += 1; // serviços são mais diretos
    if (timeForMoney)
        effortScore -= 1; // time-for-money tende exigir mais tempo
    // Notas específicas de operação
    const opNotes = model.notes?.['op_complexity'] ?? 5;
    effortScore += (8 - opNotes) / 2; // complexidade 8+ = esforço alto
    // Quanto MENOS inverso: IEN é 10 - esforço
    // então se esforçoScore é 7, IEN = 10 - 7 = 3
    // mas queremos que baixo esforço = alta pontuação
    // Vamos mapear: effortScore 1-10 → IEN 10-1 (invertido)
    const inverted = 10 - effortScore;
    return clamp(inverted, 0, 10);
}
/**
 * SFP – Score Final Personalizado
 */
export function calculateSFP(model, profile, weightProfile) {
    const imn = calcIMN(model);
    const iap = calcIAP(model, profile);
    const icp = calcICP(model, profile);
    const ird = calcIRD(model, profile);
    const ien = calcIEN(model);
    const sfp = clamp(imn * weightProfile.weights.imn +
        iap * weightProfile.weights.iap +
        icp * weightProfile.weights.icp +
        ird * weightProfile.weights.ird +
        ien * weightProfile.weights.ien, 0, 10);
    return {
        modelId: model.id,
        indices: { imn, iap, icp, ird, ien },
        sfp,
        recommendation: generateRecommendation(model, { imn, iap, icp, ird, ien }, profile, sfp),
    };
}
export function generateRecommendation(model, indices, profile, sfp) {
    const name = model.shortName || model.name;
    const parts = [];
    const tech = profile.attributes.find(a => a.attributeId === 'tech_skill')?.value;
    const prog = profile.attributes.find(a => a.attributeId === 'programming')?.value;
    const time = profile.attributes.find(a => a.attributeId === 'free_time')?.value;
    const highIMN = indices.imn >= 8;
    const highIAP = indices.iap >= 7;
    const lowIAP = indices.iap <= 4;
    const highIEN = indices.ien >= 7;
    const lowIEN = indices.ien <= 4;
    parts.push(`${name} ficou com SFP ${sfp.toFixed(1)}/10`);
    if (highIMN && indices.imn >= 8) {
        parts.push(`pela alta escalabilidade e/ou margem (${indices.imn}/10)`);
    }
    else if (indices.imn <= 4) {
        parts.push(`mas com baixa potencialidade de negócio (${indices.imn}/10)`);
    }
    if (lowIAP && prog !== undefined) {
        parts.push(`nota reduzida pelo seu nível de programação (${prog}/10)`);
    }
    if (lowIEN) {
        parts.push(`e alto esforço necessário (${indices.ien}/10)`);
    }
    else if (highIEN) {
        parts.push(`com baixo esforço inicial (${indices.ien}/10)`);
    }
    if (tech !== undefined && time !== undefined) {
        if (prog !== undefined && prog <= 4) {
            parts.push(`Se aprofundar em habilidades técnicas, ${name} sobe para ${(sfp + 0.5).toFixed(1)}`);
        }
    }
    return parts.join(', ') + '.';
}
export function evaluateAllModels(profile, weightProfile) {
    return BUSINESS_MODELS.map(m => calculateSFP(m, profile, weightProfile))
        .sort((a, b) => b.sfp - a.sfp);
}
export { BUSINESS_MODELS, CRITERIA };
